import {
  AromaMemoBadgeVariantProps,
  AromaTagVariant,
} from "@zenchef/styled-system/recipes";
import {
  MemoBadgeDescription,
  MemoBadgeMenuDatesWrap,
  MemoBadgeMenuTrigger,
  MemoBadgeWrap,
  Title,
} from "./memoBadgeParts";
import { Text } from "../typography/Text";
import Tag from "../Tag/Tag";
import Icon from "../Icon/Icon";
import { HStack } from "@zenchef/styled-system/jsx";
import ContextualMenu from "../ContextualMenu/ContextualMenu";
import Button from "../Button/Button";
import { IconName } from "@zenchef/icomoon/dist/aromaticons/types";
import { cx } from "@zenchef/styled-system/css";
import { ElementRef, forwardRef } from "react";

interface MemoActionButton {
  label: string;
  value: string;
  onClick: () => void;
  icon: IconName;
  className?: string;
  testId?: string;
}

export interface MemoBadgeProps extends AromaMemoBadgeVariantProps {
  startDate: string;
  title?: string;
  description?: string;
  actions?: MemoActionButton[];
  className?: string;
  showIcon?: boolean;
  onClick?: () => void;
}

const MemoBadge = forwardRef<ElementRef<"div">, MemoBadgeProps>(
  (
    { title, description, startDate, actions, className, showIcon, ...props },
    ref,
  ) => (
    <MemoBadgeWrap {...props} className={cx(className, "group")} ref={ref}>
      <HStack width="100%" gap="gap.1">
        {showIcon && <Icon name="memo" fontSize="88" />}
        {title ? <Title textStyle="title.xs">{title}</Title> : null}
      </HStack>
      <MemoBadgeDescription>
        {description ? (
          <Text textStyle="paragraph.m.regular" whiteSpace="pre-line">
            {description}
          </Text>
        ) : null}
      </MemoBadgeDescription>
      <MemoBadgeMenuDatesWrap>
        <HStack gap="gap.2" flexWrap="wrap">
          {startDate ? (
            <Tag
              hierarchy="bolder"
              size="small"
              colorTheme={
                (props.disabled
                  ? "disabled"
                  : props.colorTheme) as AromaTagVariant["colorTheme"]
              }
            >
              <Text textStyle="mono.s.regular">{startDate}</Text>
            </Tag>
          ) : null}
        </HStack>
        {actions ? (
          <ContextualMenu
            portalled
            zIndex="1300"
            trigger={
              <MemoBadgeMenuTrigger>
                <Button
                  iconOnly
                  hierarchy="reversed-bold"
                  size="small"
                  testId="memo-badge-menu"
                >
                  <Icon name="dots-vertical" />
                </Button>
              </MemoBadgeMenuTrigger>
            }
          >
            {actions.map(
              ({ label, value, onClick, icon, testId, className }) => (
                <ContextualMenu.Item
                  key={value}
                  label={label}
                  icon={icon}
                  value={value}
                  onClick={onClick}
                  data-testid={testId}
                  className={className}
                />
              ),
            )}
          </ContextualMenu>
        ) : null}
      </MemoBadgeMenuDatesWrap>
    </MemoBadgeWrap>
  ),
);

export default MemoBadge;
